import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_CHAT_ORDERS_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions } from "../common/warrantyAccess";
import { Loading, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { getChatOrder } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDateTime, formatMinor } from "../common/format";
import {
  coveredLabel,
  deliveryKindLabel,
  partLabel,
  partOrderActorLabel,
  partOrderEventLabel,
  partOrderStatusLabel,
} from "../common/opsLabels";
import "../warranty.css";

const Facts = ({ rows }) => (
  <dl className="warranty-facts">
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <dt>{label}</dt>
        <dd>{value === null || value === undefined || value === "" ? "-" : value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const Panel = ({ title, note, children }) => (
  <section className="warranty-panel" aria-label={title}>
    <h5>
      {title}
      {note && <span className="warranty-muted"> {note}</span>}
    </h5>
    {children}
  </section>
);

// The event detail keys this screen shows. why: "no name, address or phone" must not rest on the
// service alone, so any other key is dropped here, and a nested value (a delivery block could carry
// an address) is shown as "-" rather than opened.
const DETAIL_KEYS = [
  "part",
  "itemCode",
  "itemName",
  "bomItemCode",
  "amountMinor",
  "payableMinor",
  "cpMinor",
  "refundAmountMinor",
  "currency",
  "covered",
  "deliveryKind",
  "delivery",
  "dealerFranchiseId",
  "omsOrderCode",
  "ticketNumber",
  "paymentId",
  "provider",
  "providerOrderId",
  "providerPaymentId",
  "labelReading",
  "oldBatterySerial",
  "status",
  "reason",
  "outcome",
];

// The allow-listed details as plain text, one "key: value" per line. Amounts in minor units are
// shown as money. Server strings are text only.
const detailLines = (details, currency) => {
  if (details === null || typeof details !== "object" || Array.isArray(details)) {
    return [];
  }
  return DETAIL_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(details, key))
    .map((key) => [key, details[key]])
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => {
      if (typeof value === "object") {
        return `${key}: -`;
      }
      if (/Minor$/.test(key) && typeof value === "number") {
        const money = typeof details.currency === "string" ? details.currency : currency;
        return `${key}: ${formatMinor(value, money)}`;
      }
      return `${key}: ${String(value)}`;
    });
};

const EventDetails = ({ details, currency }) => {
  const lines = detailLines(details, currency);
  if (lines.length === 0) {
    return "-";
  }
  return (
    <ul className="warranty-plain-list">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
};

// One chat order and its whole history, as em-platform recorded it. Read only.
const ChatOrderDetail = () => {
  const { roomId: partOrderId } = useParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.OPS, WARRANTY_PERMISSION.VIEW]);
  useWarrantyNav(allowed);

  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  const mounted = useRef(true);
  const seq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setView({ status: "loading", data: null, message: "" });
    const result = await getChatOrder(accessToken, partOrderId);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      setView({ status: "ready", data: result.data || {}, message: "" });
    } else {
      setView({ status: "error", data: null, message: result.message, notFound: result.notFound });
    }
  }, [accessToken, partOrderId]);

  useEffect(() => {
    if (allowed) {
      load();
    }
  }, [allowed, load]);

  const order = view.data;
  const currency = order?.currency || "INR";
  const events = Array.isArray(order?.events) ? order.events : [];

  return (
    <WarrantyPage
      title={order?.frameNumber ? `Chat order: ${order.frameNumber}` : "Chat order"}
      sub="Placed by the support bot in chat. Read only."
      allowed={allowed}
      deniedTitle="You do not have access to chat orders"
      deniedText="Ask an admin to give you the Warranty Operations or Warranty view permission."
      actions={
        <Link className="warranty-inline-btn" to={WARRANTY_CHAT_ORDERS_URL}>
          Back to chat orders
        </Link>
      }
    >
      {view.status === "loading" && <Loading label="Loading the chat order" />}
      {view.status === "error" && <ErrorState message={view.message} onRetry={view.notFound ? undefined : load} />}
      {view.status === "ready" && (
        <>
          <Panel title="Order">
            <Facts
              rows={[
                ["Part order id", order.partOrderId],
                ["Created", formatDateTime(order.createdAt)],
                ["Frame number", order.frameNumber],
                ["Part", partLabel(order.part)],
                ["Item", order.itemName ? `${order.itemName} (${order.itemCode || "-"})` : order.itemCode],
                ["Covered or paid", coveredLabel(order.covered)],
                ["Amount", formatMinor(order.payableMinor, currency)],
                ["Delivery", deliveryKindLabel(order.deliveryKind)],
                ["Status", partOrderStatusLabel(order.status)],
                ["OMS order", order.omsOrderCode],
                ["Ticket", order.ticketNumber],
                ["Chat", order.conversationId],
              ]}
            />
          </Panel>

          <Panel title="Event history" note="as recorded, one row per change">
            {events.length === 0 ? (
              <div className="warranty-muted">Nothing recorded yet.</div>
            ) : (
              <div className="tableView warranty-ops-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">What</th>
                      <th scope="col">By</th>
                      <th scope="col">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => (
                      <tr key={`${event.at}-${event.kind}-${index}`}>
                        <td className="warranty-nowrap">{formatDateTime(event.at)}</td>
                        <td>{partOrderEventLabel(event.kind)}</td>
                        <td className="warranty-nowrap">{partOrderActorLabel(event.actor)}</td>
                        <td>
                          <EventDetails details={event.details} currency={currency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </WarrantyPage>
  );
};

export default ChatOrderDetail;
